// src/pages/AscMapping.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Card, CardContent, Typography, Autocomplete, TextField, CircularProgress,
  Alert, Button, Table, TableHead, TableRow, TableCell, TableBody, Paper, IconButton, TableContainer
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { BASE_URL } from "../../App";
import { useNavigate } from 'react-router-dom';

const AscMapping = () => {
  const [branchOptions, setBranchOptions] = useState([]);
  const [ascOptions, setAscOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedAsc, setSelectedAsc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [error, setError] = useState("");
  const [tableData, setTableData] = useState([]);
  const [dealerGridData, setDealerGridData] = useState([]);
  const [dealerLoading, setDealerLoading] = useState(false);
  
const navigate = useNavigate();

  useEffect(() => {
    const fetchBranches = async () => {
      setLoading(true);
      try {
        const response = await axios.post(`${BASE_URL}/getBranches`);
        setBranchOptions(response.data.branches || []);
      } catch (err) {
        setError("Failed to fetch branches.");
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchAscCodes = async () => {
      if (!selectedBranch) return;
      setLoading(true);
      try {
        const response = await axios.post(`${BASE_URL}/getbyBranchforascmapping`, {
          branch: selectedBranch,
        });

        if (response.data?.ascList) {
          const formatted = response.data.ascList.map((item) => ({
            id: item.code,
            name: item.name,
          }));
          setAscOptions(formatted);
          setError("");
        } else {
          setAscOptions([]);
          setError("No ASC codes found for this branch.");
        }
      } catch (err) {
        setError("Failed to fetch ASC codes.");
      } finally {
        setLoading(false);
      }
    };
    fetchAscCodes();
  }, [selectedBranch]);

 useEffect(() => {
  const fetchDealerGrid = async () => {
    if (!selectedBranch) return;
    setDealerLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/getDealerGrid`, {
        branch_code: selectedBranch.BRANCH_CODE
      });

      const formatted = (response.data?.data || []).map(dealer => ({
        dealercode: dealer.dealercode,
        dealername: dealer.dealer_name,
        active: dealer.isActive ? "1" : "0",
      }));

      setDealerGridData(formatted);
    } catch (err) {
      console.error("Failed to fetch dealer grid data", err);
    } finally {
      setDealerLoading(false);
    }
  };

  fetchDealerGrid();
}, [selectedBranch]);


  const handleView = async () => {
    if (!selectedAsc?.id) {
      setError("Please select a valid ASC code.");
      return;
    }

    setError("");
    setViewLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/getDascData`, {
        asc_code: selectedAsc.id,
      });

      if (response.data.status && response.data.data.length > 0) {
        setTableData(response.data.data);
      } else {
        setTableData([]);
        setError(response.data.message || "No data found.");
      }
    } catch (err) {
      setError("Failed to fetch ASC mapping data.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleDelete = async (index) => {
    const row = tableData[index];
    const { dasc_code, branchcode, dealercode, active } = row;

    try {
      const response = await axios.post(`${BASE_URL}/deleteDascMapping`, {
        dasc_code, branchcode, dealercode, active,
      });

      if (response.data.status) {
        const updatedData = [...tableData];
        updatedData.splice(index, 1);
        setTableData(updatedData);
        setError("");
        alert("Row deleted successfully!");
      } else {
        setError(response.data.message || "Failed to delete row.");
      }
    } catch (err) {
      setError("Failed to delete row.");
    }
  };

  const handleAddMapping = async () => {
    if (!selectedBranch?.BRANCH_CODE || !selectedAsc?.id) {
      alert("Please select both branch and ASC code.");
      return;
    }

    const selectedDealers = dealerGridData
      .filter(dealer => dealer.active === "1")
      .map(dealer => dealer.dealercode);

    if (selectedDealers.length === 0) {
      alert("No dealers selected for mapping.");
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}/saveDascMapping`, {
        branchcode: selectedBranch.BRANCH_CODE,
        asc_code: selectedAsc.id,
        dealers: selectedDealers
      });

      if (response.data.status) {
        alert("Mapping saved successfully!");
        handleView(); 
      } else {
        alert("Failed to save mapping: " + response.data.message);
      }
    } catch (err) {
      alert("An error occurred while saving mapping.");
    }
  };

  const handleOpenPopup = () => {
    if (!selectedBranch?.BRANCH_CODE) {
      alert("Please select a branch first.");
      return;
    }

   // const url = `/home/Service/Un_Mapped_Dealers?branchCode=${selectedBranch.BRANCH_CODE}`;
   console.log("Navigating with branchCode:", selectedBranch.BRANCH_CODE);
     navigate(`/home/Service/Un_Mapped_Dealers?branchCode=${selectedBranch.BRANCH_CODE}`);

   // window.open(url, "_blank", "width=800,height=600,scrollbars=yes");
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
      <Card elevation={4} sx={{ borderRadius: 4 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            ASC Mapping
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Autocomplete
            options={branchOptions}
            getOptionLabel={(option) => option.BRANCH_NAME || ""}
            value={selectedBranch}
            onChange={(e, value) => {
              setSelectedBranch(value);
              setSelectedAsc(null);
              setTableData([]);
            }}
            renderInput={(params) => <TextField {...params} label="Select Branch" fullWidth />}
            sx={{ mb: 3 }}
            disabled={loading}
          />

          <Autocomplete
            options={ascOptions}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedAsc}
            onChange={(e, value) => {
              setSelectedAsc(value);
              setTableData([]);
            }}
            renderInput={(params) => <TextField {...params} label="Select ASC Code" fullWidth />}
            disabled={!selectedBranch || loading}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleView}
            disabled={viewLoading || !selectedAsc}
            sx={{ mt: 2 }}
          >
            {viewLoading ? "Loading..." : "View"}
          </Button>

         {dealerGridData.length > 0 && (
  <Paper sx={{ mt: 5, p: 2 }}>
    <Typography variant="h6" gutterBottom>Dealer List</Typography>
    <TableContainer sx={{ maxHeight: 300 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}>
              Dealer Code
            </TableCell>
            <TableCell sx={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}>
              Dealer Name
            </TableCell>
            <TableCell sx={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}>
              Active
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dealerGridData.map((dealer, index) => (
            <TableRow key={index}>
              <TableCell>{dealer.dealercode}</TableCell>
              <TableCell>{dealer.dealername}</TableCell>
              <TableCell>
                <input
                  type="checkbox"
                  checked={dealer.active === "1"}
                  onChange={(e) => {
                    const updated = [...dealerGridData];
                    updated[index].active = e.target.checked ? "1" : "0";
                    setDealerGridData(updated);
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
)}


          {tableData.length > 0 && (
            <>
              <Paper sx={{ mt: 4 }}>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Branch Code</TableCell>
                        <TableCell>ASC Code</TableCell>
                        <TableCell>Dealer Code</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.branchcode}</TableCell>
                          <TableCell>{row.dasc_code}</TableCell>
                          <TableCell>{row.dealercode}</TableCell>
                          <TableCell>{row.active === "1" ? "Active" : "Inactive"}</TableCell>
                          <TableCell>
                            <IconButton color="error" onClick={() => handleDelete(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Box display="flex" justifyContent="center" gap={2} mt={4}>
                <Button variant="contained" color="success" onClick={handleAddMapping}>
                  Add Mapping
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleOpenPopup}>
                  Unmapped Dealers
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AscMapping;
